// apps/spa/src/hooks/useRosterExport.ts
// Mutation hook for POST /api/roster-export/run — drives the "Run roster export"
// menu item in OfficerMenu. Server enforces the specialist-tier gate; this hook
// just surfaces success/failure as a sonner toast.
//
// On success: toast with member count + a "View thread" action that opens the
// freshly-created Discord forum thread in a new tab.
// On 403 ROLE_REQUIRED: the global api.ts handler redirects to /auth/role-required
// (consistent with all other role-gated mutations across the SPA).
// On any other failure: red error toast.

import { useMutation } from '@tanstack/react-query';
import { toast } from 'sonner';
import { api } from '@/lib/api';

export type RosterExportResponse = {
  ok: true;
  membersExported: number;
  onboardingHolders: number;
  onboardingOverdue: number;
  zeroRoleMembers: number;
  threadId: string;
  messageId: string;
  threadUrl: string;
};

export function useRosterExport() {
  return useMutation({
    mutationFn: () =>
      api<RosterExportResponse>('/api/roster-export/run', { method: 'POST' }),
    onSuccess: (result) => {
      toast.success(
        `Roster export posted — ${result.membersExported} members, ${result.onboardingOverdue} onboarding > 14 days`,
        {
          action: {
            label: 'View thread',
            onClick: () => window.open(result.threadUrl, '_blank', 'noopener,noreferrer'),
          },
          duration: 10_000,
        },
      );
    },
    onError: () => {
      toast.error("Couldn't post the roster export. Check Workers Logs for details.");
    },
  });
}
